import { useContext } from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Grid, Icon, LinearProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import { CustomPagination } from 'utils/functions';
import {
  dropTipoExame,
  dropResultado,
  dropOrdemExame,
  dropIndicadorResultado,
} from 'utils/drops';
import { useDialog } from 'components/Modals';
import { GridContext } from 'contexts/GridContext';
import InputMask from 'components/InputMask';
import Dropdown from 'components/Dropdown';
import Input from 'components/Input';
import Button from 'components/Button';
import Title from '../Components/Title';
import localeText from 'utils/localeText';
import moment from 'moment';

const Saude = ({ drops, loading, pessoal = {}, onSubmit, onDelete }) => {
  const { openDialog } = useDialog();
  const saudes = pessoal?.Saudes ?? [];

  const Modal = ({ item }) => {
    const defaultValues = {
      rotina: 'PessoalSaude',
      id: item?.id,
      pessoal_id: pessoal?.id,
      tpexameocup: item?.tpexameocup || null,
      dtaso: item?.dtaso || null,
      resaso: item?.resaso || null,
      dtexm: item?.dtexm || null,
      procedimento_diagnostico_id: item?.procedimento_diagnostico_id || null,
      obsproc: item?.obsproc || '',
      ordexame: item?.ordexame || null,
      indresult: item?.indresult || null,
      nmmed: item?.nmmed || '',
      cpfmed: item?.cpfmed || '',
      nismed: item?.nismed || '',
      nrcrm: item?.nrcrm || '',
      ufcrm_id: item?.ufcrm_id || null,
      nmresp: item?.nmresp || '',
      cpfresp: item?.cpfresp || '',
      nrcrmresp: item?.nrcrmresp || '',
      ufcrmresp_id: item?.ufcrmresp_id || null,
    };
    const { postLoading } = useContext(GridContext);
    const { control, handleSubmit } = useForm({ defaultValues });

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Title title="Informações do Exame" disableMargin />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Dropdown
            control={control}
            name="tpexameocup"
            options={dropTipoExame}
            label="Tipo de Exame Ocupacional"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask
            type="date"
            name="dtexm"
            control={control}
            label="Data do Exame"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Dropdown
            name="resaso"
            control={control}
            options={dropResultado}
            label="Resultado do ASO"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputMask
            type="date"
            name="dtaso"
            control={control}
            label="Data do ASO"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Dropdown
            control={control}
            label="Procedimento Diagnóstico"
            name="procedimento_diagnostico_id"
            options={drops?.ProcedimentoDiagnostico}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Dropdown
            name="ordexame"
            control={control}
            label="Ordem do Exame"
            options={dropOrdemExame}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Dropdown
            name="indresult"
            control={control}
            label="Indicador do Resultado"
            options={dropIndicadorResultado}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            rows={3}
            multiline
            name="obsproc"
            control={control}
            label="Observação"
          />
        </Grid>
        <Grid item xs={12}>
          <Title title="Médico Examinador" />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Input name="nmmed" label="Nome" control={control} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputMask
            label="CPF"
            name="cpfmed"
            control={control}
            mask="999.999.999-99"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Input name="nismed" label="NIS" control={control} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Input name="nrcrm" label="CRM" control={control} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Dropdown
            label="UF"
            name="ufcrm_id"
            control={control}
            options={drops?.UF}
          />
        </Grid>
        <Grid item xs={12}>
          <Title title="Médico Responsável PCMSO" />
        </Grid>
        <Grid item xs={12} sm={8}>
          <Input name="nmresp" label="Nome" control={control} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <InputMask
            label="CPF"
            name="cpfresp"
            control={control}
            mask="999.999.999-99"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Input name="nrcrmresp" label="CRM" control={control} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Dropdown
            label="UF"
            control={control}
            name="ufcrmresp_id"
            options={drops?.UF}
          />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Button
            color="primary"
            loading={postLoading}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            Salvar
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Title
          title="Saúde do Trabalhador"
          disableMargin
          rightButton={
            <Button
              color="primary"
              variant="outlined"
              onClick={() => openDialog(<Modal />, 'Adicionar Exame')}
            >
              Adicionar
            </Button>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <DataGrid
          rows={saudes}
          columns={[
            {
              field: 'tpexameocup',
              headerName: 'Tipo de Exame',
              minWidth: 300,
              flex: 1,
              sortable: false,
            },
            {
              field: 'dtaso',
              headerName: 'Data ASO',
              width: 200,
              sortable: false,
              valueGetter: ({ value }) =>
                value && moment(value).format('DD/MM/YYYY'),
            },
            {
              field: 'resaso',
              headerName: 'Resultado ASO',
              width: 300,
              sortable: false,
            },
            {
              field: 'procedimento_diagnostico',
              headerName: 'Procedimento diagnóstico',
              width: 300,
              sortable: false,
            },
            {
              field: 'nmmed',
              headerName: 'Médico Examinador',
              width: 300,
              sortable: false,
            },
            {
              field: 'actions',
              headerName: 'Ações',
              type: 'actions',
              width: 100,
              getActions: ({ row }) => [
                <GridActionsCellItem
                  icon={<Icon>edit</Icon>}
                  label="Editar"
                  onClick={() =>
                    openDialog(<Modal item={row} />, 'Editar Exame')
                  }
                />,
                <GridActionsCellItem
                  icon={<Icon>delete</Icon>}
                  label="Excluir"
                  onClick={() =>
                    onDelete({ rotina: 'PessoalSaude', id: row?.id })
                  }
                />,
              ],
            },
          ]}
          autoHeight
          pagination
          pageSize={10}
          density="compact"
          loading={loading}
          disableColumnMenu
          showCellRightBorder
          showColumnRightBorder
          disableSelectionOnClick
          localeText={localeText}
          getRowHeight={() => 'auto'}
          components={{
            Pagination: CustomPagination,
            LoadingOverlay: LinearProgress,
          }}
        />
      </Grid>
    </Grid>
  );
};

export default Saude;
